import React, { memo } from "react"
import PropTypes from "prop-types"
import { Button } from "antd"
import { LeftOutlined, RightOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { generateTimeSlots } from "../../utils/calendarHelpers"
import styles from "../../EquipmentCalendar.module.css"
import { Boat } from "ui/Icon/DSIcon/Boat"
import { Text } from "ui/Typography"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { DSIcon } from "ui/Icon/DSIcon"
import { getDefaultCalendarColor, getPrimaryCalendarTextColor, resolveBookingColors } from "pagesV2/pro/Calendar/calendarColorUtils"
import { getEventColor } from "pagesV2/pro/Calendar/utils"
import { useTheme } from "context/AntdThemeContext"

const ROW_HEIGHT = 48

const renderBookingBlock = (booking, defaultColor, primaryTextColor, onBookingClick) => {
  const previewColors = resolveBookingColors(booking.color, defaultColor, primaryTextColor, getEventColor)
  return (
    <button
      key={booking.id}
      className={styles.bookingBlock}
      style={{
        backgroundColor: previewColors.backgroundColor,
        borderColor: previewColors.borderColor,
        top: `${booking.startTime * ROW_HEIGHT + 2}px`,
        height: `${booking.duration * ROW_HEIGHT - 4}px`,
      }}
      onClick={() => onBookingClick(booking.uuid)}
      aria-label={`Open booking ${booking.tripName} at ${booking.timeRange}`}
      type="button"
    >
      <Text fw={700} variant="caption" className={styles.bookingTime} color={previewColors.textColor || primaryTextColor}>
        {booking.timeRange}
      </Text>
      <Text className={`${styles.bookingTrip} ${styles.ellipsisText} ellipsis`} color={previewColors.textColor || primaryTextColor}>
        {booking.tripName}
      </Text>
    </button>
  )
}

/**
 * Day view calendar component
 * Displays a daily calendar grid with time slots and assets
 */
const DayView = ({ assets, dayGridRef, showLeftArrow, showRightArrow, handleScroll, handleHeaderScroll, onBookingClick }) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const timeSlots = generateTimeSlots()
  const defaultColor = getDefaultCalendarColor()
  const primaryTextColor = getPrimaryCalendarTextColor(theme)

  // Show empty state if no assets
  if (!assets || assets.length === 0) {
    return (
      <WithEmptyStateWrapper
        name="calendar-assets"
        hasItems={false}
        fallbackTitle={t("equipment.calendar.no_assets", { defaultValue: "No assets found" })}
        fallbackDescription={t("equipment.calendar.no_assets_description", {
          defaultValue: "Try adjusting your filters or add new assets"
        })}
        fallbackImageSrc={<DSIcon name="boat" width={80} height={80} />}
      >
        <div />
      </WithEmptyStateWrapper>
    )
  }

  return (
    <div className={styles.calendarGrid}>
      {/* Header Row */}
      <div className={styles.gridHeader}>
        {/* Fixed Time Column Header */}
        <div className={styles.timeColumnHeader}>
          <Boat />
        </div>

        {/* Scrollable Asset Headers Container */}
        <div className={styles.assetHeadersContainer}>
          {/* Left Arrow */}
          {showLeftArrow && (
            <Button icon={<LeftOutlined />} className={styles.scrollArrowLeft} onClick={() => handleScroll("left")} shape="circle" />
          )}

          {/* Scrollable Asset Headers */}
          <div className={styles.assetHeadersScroll} ref={dayGridRef} onScroll={handleHeaderScroll}>
            {assets.map((asset) => (
              <div key={asset.id} className={styles.dayAssetColumnHeader}>
                <Text fw={500}>
                  {t("equipment.calendar.asset")} #{asset.number}
                </Text>
              </div>
            ))}
          </div>

          {/* Right Arrow */}
          {showRightArrow && (
            <Button icon={<RightOutlined />} className={styles.scrollArrowRight} onClick={() => handleScroll("right")} shape="circle" />
          )}
        </div>
      </div>

      {/* Time Slots Grid */}
      <div className={styles.gridBody}>
        <div className={styles.gridBodyInner}>
          {/* Time lines column */}
          <div className={styles.timeColumn}>
            {timeSlots.map((timeSlot) => (
              <div key={timeSlot} className={styles.timeLabel}>
                <Text>{timeSlot}</Text>
              </div>
            ))}
          </div>

          {/* Asset columns with absolute booking blocks */}
          <div className={styles.assetColumnsContainer}>
            <div className={styles.assetColumnsScroll}>
              {/* Grid lines overlay */}
              <div className={styles.gridLinesOverlay}>
                {timeSlots.map((timeSlot) => (
                  <div key={timeSlot} className={styles.gridLine} />
                ))}
              </div>

              {/* Asset columns */}
              {assets.map((asset) => (
                <div
                  key={asset.id}
                  className={styles.assetColumn}
                  style={{ height: `${timeSlots.length * ROW_HEIGHT}px` }}
                >
                  {asset.bookings?.map((booking) =>
                    renderBookingBlock(booking, defaultColor, primaryTextColor, onBookingClick)
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

DayView.propTypes = {
  assets: PropTypes.array.isRequired,
  dayGridRef: PropTypes.object.isRequired,
  showLeftArrow: PropTypes.bool.isRequired,
  showRightArrow: PropTypes.bool.isRequired,
  handleScroll: PropTypes.func.isRequired,
  handleHeaderScroll: PropTypes.func.isRequired,
  onBookingClick: PropTypes.func.isRequired
}

export default memo(DayView)
